import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import { ConnectionStatus, ConnectionType, IConnectionDetailsDTO } from '../../../../../api/connections.dao';
import ChevronSVG from '../../../../../images/svg/icons8-expand-arrow';
import { getConnectionItemLabelText } from '../../../../../infrastructure/utils/connections.utils';
import Text from '../../../../global/text/text';
import { MultiSelectOption } from '../../../../global/multi-select-list/_multi-select-option';
import ColumnWithCheckbox from '../../../../global/column-with-checkbox/column-with-checkbox';
import { WebinarReportsViewModel } from '../webinar-report/webinar-reports.viewmodel';
import { ConnectionDeleteDetails } from './_connection-delete-message';
import { ConnectionDetailsPopover } from './_connection-details-popover';
import { CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, OpenFrom } from '../../../../../tracking-service/tracking-values';
import { MeetingApiParams } from './_connection-form/connection-form.viewmodel';
import { ConnectionVersion } from '../../../../../constants/common.constants';

interface IProps {
    connection: IConnectionDetailsDTO;
    cloneConnection: (connection: IConnectionDetailsDTO) => void;
    handleOnChange: (connection: IConnectionDetailsDTO) => void;
    showConfigurePassportForm: (connection: IConnectionDetailsDTO) => void;
    setDeletePopoverConnection: (connection: IConnectionDetailsDTO | null) => void;
    isDefault?: boolean;
    onConnectionStatusChange?: (meetingConnectionId: number) => void;
    eventTimezone: string;
    controlledCheckbox?: boolean;
    checkbox: boolean;
    webinarReportsVM?: WebinarReportsViewModel;
    handleTrackingEvent?: (event: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS) => void;
    handleTroubleshoot: (connection: IConnectionDetailsDTO) => void;
    trackHostJoinClick: (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, openFrom: OpenFrom) => void;
    hideChevron?: boolean;
    meetingParams: MeetingApiParams;
    onOpenKnovioPreviewLinkPopover: () => void;
    shouldDisableDeleteButton: boolean;
    knovioPreCheckLoading?: boolean;
    connectionVersion?: ConnectionVersion;
    openFrom?: OpenFrom;
}

const ConnectionDetailsItem = (props: IProps) => {
    const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
    const listItemRef = useRef(null);
    const deleteItemRef = React.useRef<HTMLDivElement>(null);

    const {
        connection,
        eventTimezone,
        controlledCheckbox,
        cloneConnection,
        onConnectionStatusChange,
        isDefault,
        checkbox,
        webinarReportsVM,
        handleTrackingEvent,
        trackHostJoinClick,
        hideChevron,
        meetingParams,
        onOpenKnovioPreviewLinkPopover,
        shouldDisableDeleteButton,
        knovioPreCheckLoading,
        connectionVersion,
        openFrom
    } = props;

    const handleOpenPopover = e => {
        e.stopPropagation();
        const newState = !isPopoverOpen;
        setIsPopoverOpen(newState);
        if (newState && handleTrackingEvent) {
            handleTrackingEvent(CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS.CALL);
        }
    };

    const onClickOutside = (state: boolean) => {
        setIsPopoverOpen(state);
    };

    const isCancelledConnection = connection?.cancelled || false;
    const isV2PanelistConnection = connection.type === ConnectionType.PanelistLink && connectionVersion === ConnectionVersion.V2;

    return (
        <>
            {(checkbox && !isCancelledConnection && !isV2PanelistConnection) && (controlledCheckbox ?
                <MultiSelectOption
                    isSelected={connection.connectionStatus === ConnectionStatus.STARTED}
                    onClick={() => onConnectionStatusChange(connection.meetingConnectionId)}
                    isHidden={isV2PanelistConnection} />
                :
                <ColumnWithCheckbox
                    isActive={connection.connectionStatus === ConnectionStatus.STARTED}
                    isHidden={isV2PanelistConnection}
                    withGreyedCheckMark />
            )}
            {
                isCancelledConnection && (
                    <div ref={deleteItemRef}>
                        <ConnectionDeleteDetails
                            eventTimezone={eventTimezone}
                            isCancelled={isCancelledConnection}
                            cancelDateTime={connection.cancelDateTime}
                            listItemRef={deleteItemRef}
                        />
                    </div>
                )
            }
            <div
                ref={listItemRef}
                className={classnames('connection-list__item', {
                    'connection-list__item--active': isPopoverOpen,
                    'connection-list__item--not-started': connection.connectionStatus !== ConnectionStatus.STARTED,
                    'connection-list__item--cancelled': isCancelledConnection,
                    'connection-list__item--hover': !isCancelledConnection && openFrom === OpenFrom.RunMeeting,
                    'connection-list__item--child-connection': isV2PanelistConnection && openFrom === OpenFrom.Connections,
                    'connection-list__item--controltab-child-connection': isV2PanelistConnection && openFrom === OpenFrom.Control
                })}
                onClick={handleOpenPopover}
            >
                <Text tag='p' message={getConnectionItemLabelText(connection)}
                    withEllipsis className={classnames({ 'connection-list__item--delete': isCancelledConnection })} />
                {isDefault && <span className='connection-list-item__default' />}
                {!hideChevron && <div className='attendee-button__chevron'>
                    <ChevronSVG />
                </div>}
            </div>
            <div className='connection-item-popover'>
                <ConnectionDetailsPopover
                    {...props}
                    knovioPreCheckLoading={knovioPreCheckLoading}
                    cloneConnection={cloneConnection}
                    isPopoverOpen={isPopoverOpen}
                    setIsPopoverOpen={onClickOutside}
                    listItemRef={listItemRef}
                    webinarReportsVM={webinarReportsVM}
                    trackHostJoinClick={trackHostJoinClick}
                    meetingParams={meetingParams}
                    onOpenKnovioPreviewLinkPopover={onOpenKnovioPreviewLinkPopover}
                    shouldDisableDeleteButton={shouldDisableDeleteButton}
                />
            </div>
        </>
    );
};

export { ConnectionDetailsItem };
